// /utils/okxWebSocket.js

class WebSocketManager {
    constructor(url, options = {}) {
        this.url = url
        this.ws = null

        this.reconnectInterval = options.reconnectInterval || 5000
        this.maxReconnectInterval = options.maxReconnectInterval || 30000
        this.heartbeatInterval = options.heartbeatInterval || 20000

        this.isManualClose = false
        this.reconnectTimer = null
        this.heartbeatTimer = null
        this.reconnectCount = 0
        this.isConnected = false

        // ✅ 订阅缓存
        this.subscriptions = new Set()

        // ✅ login 方法（外部注入）
        this.loginFn = null

        this.eventHandlers = {
            onMessage: null,
            onOpen: null,
            onClose: null,
            onError: null,
        }
    }

    // ========================
    // 连接
    // ========================
    connect() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) return

        this.isManualClose = false
        this.ws = new WebSocket(this.url)

        this.ws.onopen = async () => {
            console.log(`[WS] 连接成功: ${this.url}`)
            this.isConnected = true
            this.reconnectCount = 0

            this.startHeartbeat()

            // ✅ 自动 login
            if (this.loginFn) {
                await this.loginFn(this)
            }

            // ✅ 自动恢复订阅
            this.resubscribe()

            this.eventHandlers.onOpen && this.eventHandlers.onOpen()
        }

        this.ws.onmessage = (event) => {
            this.eventHandlers.onMessage && this.eventHandlers.onMessage(event.data)
        }

        this.ws.onclose = () => {
            console.log(`[WS] 断开连接`)
            this.isConnected = false

            this.stopHeartbeat()
            this.eventHandlers.onClose && this.eventHandlers.onClose()

            if (!this.isManualClose) {
                this.reconnect()
            }
        }

        this.ws.onerror = (err) => {
            console.error("[WS] 错误:", err)
            this.eventHandlers.onError && this.eventHandlers.onError(err)
            this.ws.close()
        }
    }

    // ========================
    // 发送
    // ========================
    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(typeof data === "string" ? data : JSON.stringify(data))
        }
    }

    // ========================
    // 订阅（自动缓存）
    // ========================
    subscribe(data) {
        const msg = JSON.stringify(data)
        this.subscriptions.add(msg)
        this.send(msg)
    }

    unsubscribe(data) {
        const msg = JSON.stringify(data)
        this.subscriptions.delete(msg)
        this.send(msg)
    }

    resubscribe() {
        if (this.subscriptions.size === 0) return

        console.log("[WS] 恢复订阅")

        this.subscriptions.forEach(msg => {
            this.send(msg)
        })
    }

    // ========================
    // 重连（指数退避）
    // ========================
    reconnect() {
        if (this.reconnectTimer) return

        const delay = Math.min(
            this.reconnectInterval * Math.pow(2, this.reconnectCount),
            this.maxReconnectInterval
        )

        console.log(`[WS] ${delay}ms 后重连`)

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            this.reconnectCount++
            this.connect()
        }, delay)
    }

    // ========================
    // 心跳
    // ========================
    startHeartbeat() {
        this.stopHeartbeat()

        this.heartbeatTimer = setInterval(() => {
            if (this.isConnected) {
                this.send("ping")
            }
        }, this.heartbeatInterval)
    }

    stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer)
            this.heartbeatTimer = null
        }
    }

    // ========================
    // 设置 login
    // ========================
    setLogin(fn) {
        this.loginFn = fn
    }

    // ========================
    // 关闭
    // ========================
    close() {
        this.isManualClose = true

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        this.stopHeartbeat()

        if (this.ws) {
            this.ws.close()
        }

        console.log("[WS] 手动关闭")
    }

    // ========================
    // 事件
    // ========================
    on(event, handler) {
        if (this.eventHandlers.hasOwnProperty(event)) {
            this.eventHandlers[event] = handler
        }
    }
}

export default WebSocketManager